import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Save, CheckCircle } from 'lucide-react';
import { Spinner } from '../components/Spinner';
import styles from './SettingsPage.module.css';

interface PaymentSettingsForm {
  instapayLink: string;
  walletNumber: string;
  walletName: string;
  whatsappNumber: string;
}

const initialForm: PaymentSettingsForm = {
  instapayLink: '',
  walletNumber: '',
  walletName: '',
  whatsappNumber: '',
};

export function PaymentSettingsPage() {
  const { token } = useAuth();
  const [formData, setFormData] = useState<PaymentSettingsForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/payment-settings`);
        const data = await response.json();
        if (data.paymentSettings) {
          setFormData({
            instapayLink: data.paymentSettings.instapayLink || '',
            walletNumber: data.paymentSettings.walletNumber || '',
            walletName: data.paymentSettings.walletName || '',
            whatsappNumber: data.paymentSettings.whatsappNumber || '',
          });
        }
      } catch (err) {
        console.error('Error fetching payment settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/payment-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save payment settings');
      }
      setSaved(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading payment settings..." size="lg" />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Payment Settings</h1>
        <p>
          The Instapay link, wallet number, and WhatsApp number shown to
          customers at checkout. Saving here updates the storefront
          immediately — no redeploy needed.
        </p>
      </header>

      <div className={styles.card}>
        {error && <div className={styles.error}>{error}</div>}
        {saved && (
          <div className={styles.success}>
            <CheckCircle size={18} />
            Saved — live on the storefront now.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label>Instapay Link</label>
            <input
              type="text"
              name="instapayLink"
              value={formData.instapayLink}
              onChange={handleChange}
              placeholder="https://ipn.eg/S/your-handle/instapay/xxxxxx"
            />
            <span className={styles.hint}>
              Customers tap this to open Instapay and pay directly.
            </span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Wallet Number</label>
              <input
                type="text"
                name="walletNumber"
                value={formData.walletNumber}
                onChange={handleChange}
                placeholder="e.g. 01012345678"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Wallet Name / Provider</label>
              <input
                type="text"
                name="walletName"
                value={formData.walletName}
                onChange={handleChange}
                placeholder="e.g. Vodafone Cash"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>WhatsApp Number (for payment screenshots)</label>
            <input
              type="text"
              name="whatsappNumber"
              value={formData.whatsappNumber}
              onChange={handleChange}
              placeholder="e.g. 201012345678 (country code, no + or spaces)"
            />
            <span className={styles.hint}>
              Used to build the "Send Screenshot on WhatsApp" link — digits only, with country code.
            </span>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Payment Settings'}
          </button>
        </form>
      </div>
    </div>
  );
}
